import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Edit,
  Trash2,
  User,
  Calendar,
  Clock,
  MapPin,
  Phone,
  Mail,
  FileText,
  TestTube,
  CheckCircle,
  RefreshCw,
  XCircle,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { format } from "date-fns";
import { useConciergeTestConfig } from "@/hooks/useConciergeTests";
import { getTestNameFromCode } from "@/utils/testMapping";

const statusConfig = {
  submitted: {
    label: "Submitted",
    icon: RefreshCw,
    color: "bg-blue-100 text-blue-700 border-blue-200",
    iconColor: "text-blue-600",
  },
  scheduled: {
    label: "Scheduled",
    icon: Calendar,
    color: "bg-purple-100 text-purple-700 border-purple-200",
    iconColor: "text-purple-600",
  },
  in_progress: {
    label: "In Progress",
    icon: RefreshCw,
    color: "bg-indigo-100 text-indigo-700 border-indigo-200",
    iconColor: "text-indigo-600",
  },
  completed: {
    label: "Completed",
    icon: CheckCircle,
    color: "bg-green-100 text-green-700 border-green-200",
    iconColor: "text-green-600",
  },
  cancelled: {
    label: "Cancelled",
    icon: XCircle,
    color: "bg-gray-100 text-gray-700 border-gray-200",
    iconColor: "text-gray-600",
  },
};

export default function RequestDetail({
  request,
  onClose,
  onEdit,
  onDelete,
  onStatusChange,
  isPatientView = false,
}) {
  const config = statusConfig[request.status] || statusConfig.submitted;
  const { data: testConfig } = useConciergeTestConfig();

  const handleDelete = () => {
    if (
      window.confirm(
        "Are you sure you want to delete this concierge testing request? This action cannot be undone."
      )
    ) {
      onDelete?.(request.id);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={onClose}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to List
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-3">
              <TestTube className="w-6 h-6 text-blue-600" />
              Concierge Test Request
            </h1>
            <p className="text-slate-600">Request ID: {request.id}</p>
          </div>
        </div>
        {!isPatientView && (
          <div className="flex gap-2">
            {onEdit && (
              <Button variant="outline" onClick={() => onEdit(request)}>
                <Edit className="w-4 h-4 mr-2" />
                Edit
              </Button>
            )}
            {onDelete && (
              <Button
                variant="outline"
                onClick={handleDelete}
                className="text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Patient Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Patient Information
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Name</label>
              <p className="text-slate-900 font-medium">
                {request.patient_name || "N/A"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">
                ID Number
              </label>
              <p className="text-slate-900">
                {request.patient_identification_number || "N/A"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">
                Date of Birth
              </label>
              <p className="text-slate-900">
                {request.patient_dob
                  ? format(new Date(request.patient_dob), "MMM d, yyyy")
                  : "N/A"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">
                Phone
              </label>
              <p className="text-slate-900 flex items-center gap-2">
                <Phone className="w-4 h-4" />
                {request.patient_phone || "N/A"}
              </p>
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-slate-700">
                Address
              </label>
              <p className="text-slate-900 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {request.patient_address || "N/A"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">
                Email
              </label>
              <p className="text-slate-900 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {request.patient_email || "N/A"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Request Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Request Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">
                Referring Doctor
              </label>
              <p className="text-slate-900">
                {request.referring_doctor || "N/A"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">
                Status
              </label>
              <div className="flex items-center gap-2">
                {!isPatientView && onStatusChange ? (
                  <Select
                    value={request.status}
                    onValueChange={(newStatus) =>
                      onStatusChange(request.id, newStatus)
                    }
                  >
                    <SelectTrigger className="w-40">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="submitted">Submitted</SelectItem>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge
                    className={`${config.color} inline-flex items-center gap-1`}
                  >
                    {React.createElement(config.icon, {
                      className: `w-4 h-4 ${config.iconColor}`,
                    })}
                    {config.label}
                  </Badge>
                )}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">
                Preferred Date
              </label>
              <p className="text-slate-900 flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {request.preferred_date
                  ? format(new Date(request.preferred_date), "MMM d, yyyy")
                  : "N/A"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">
                Preferred Time
              </label>
              <p className="text-slate-900 flex items-center gap-2 capitalize">
                <Clock className="w-4 h-4" />
                {request.preferred_time_slot || "N/A"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">
                Lab Partner
              </label>
              <p className="text-slate-900">
                {request.lab_partner || "Testaro Labs"}
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">
                Created Date
              </label>
              <p className="text-slate-900">
                {request.created_date
                  ? format(
                      new Date(request.created_date),
                      "MMM d, yyyy 'at' h:mm a"
                    )
                  : "N/A"}
              </p>
            </div>
          </div>

          {/* Clinical Notes */}
          {request.clinical_notes && (
            <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
              <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Clinical Notes
              </h4>
              <p className="text-amber-700 whitespace-pre-wrap">
                {request.clinical_notes}
              </p>
            </div>
          )}

          {/* Scheduled Visit Info */}
          {request.scheduled_visit_datetime && (
            <div className="mt-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h4 className="font-medium text-purple-800 mb-2">
                Visit Scheduled
              </h4>
              <p className="text-purple-700">
                <strong>Date & Time:</strong>{" "}
                {format(
                  new Date(request.scheduled_visit_datetime),
                  "EEEE, MMM d, yyyy 'at' h:mm a"
                )}
              </p>
              {request.assigned_nurse && (
                <p className="text-purple-700 mt-1">
                  <strong>Assigned Nurse:</strong> {request.assigned_nurse}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Requested Tests */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TestTube className="w-5 h-5" />
            Requested Tests ({request.requested_tests?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {request.requested_tests && request.requested_tests.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {request.requested_tests.map((testCode, index) => {
                const testName = getTestNameFromCode(testCode, testConfig);
                return (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="p-2 justify-start text-left font-normal"
                    title={testCode} // Show code on hover
                  >
                    {testName}
                  </Badge>
                );
              })}
            </div>
          ) : (
            <p className="text-slate-500 italic">No tests specified</p>
          )}
        </CardContent>
      </Card>

      {/* Results and Follow-up Section */}
      {(request.results_received || request.follow_up_required) && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Results & Follow-up
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700">
                  Results Received
                </label>
                <Badge
                  variant={request.results_received ? "success" : "secondary"}
                >
                  {request.results_received ? "Yes" : "No"}
                </Badge>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">
                  Shared with Patient
                </label>
                <Badge
                  variant={
                    request.results_shared_with_patient
                      ? "success"
                      : "secondary"
                  }
                >
                  {request.results_shared_with_patient ? "Yes" : "No"}
                </Badge>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">
                  Shared with Doctor
                </label>
                <Badge
                  variant={
                    request.results_shared_with_doctor ? "success" : "secondary"
                  }
                >
                  {request.results_shared_with_doctor ? "Yes" : "No"}
                </Badge>
              </div>
            </div>

            {request.follow_up_required && (
              <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                <h4 className="font-medium text-orange-800 mb-2">
                  Follow-up Required
                </h4>
                {request.follow_up_notes && (
                  <p className="text-orange-700 whitespace-pre-wrap">
                    {request.follow_up_notes}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
